import './App.css';
import {
  Routes,
  Route,
  RouterProvider,
  createBrowserRouter
} from "react-router-dom";

import PrivateRoutes from './components/private';
import AdminRoute from './components/admin-route';
import Devices from './pages/devices';
import LoginPage from './pages/public/login';
import RegisterPage from './pages/public/signup';
import ForgotPage from './pages/public/forgot';
import ResetPasswordPage from './pages/public/reset';
import OAuthLoginPage from './pages/public/oauth-login';
import OAuthConsentPage from './pages/public/oauth-consent';
import SharedView from './pages/shared';
import DeviceDetail from './pages/devices/detail';
import AddDevicePage from './pages/devices/new';
import Dashboards from './pages/dashboards/list';
import DashboardView from './pages/dashboards';
import Account from './pages/account/settings';
import Sources from './pages/sources';
import Profiles from './pages/profiles';
import ProfileSettings from './pages/profiles/settings';
import Checkout from './pages/account/checkout';
import { AuthProvider } from './services/AuthProvider';
import { ThemeProvider } from './services/ThemeProvider';
import { Crisp } from "crisp-sdk-web";
import { useEffect } from 'react';
import MqttClient from './services/ws/MqttClient';
import Users from './pages/users';
import Admin from './pages/admin';
import AdminUsers from './pages/admin/users';
import OAuthClients from './pages/admin/oauth';
import Integrations from './pages/integrations';
import * as Sentry from "@sentry/react";
import { redactShareToken } from './services/sentryRedact';

const router = createBrowserRouter([
  { path: "*", Component: Root },
]);

function Root() {
  return (
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/oauth/login" element={<OAuthLoginPage />} />
        <Route path="/oauth/consent" element={<OAuthConsentPage />} />
        <Route path="/s/:token" element={<SharedView />} />
        <Route element={<PrivateRoutes/>}>
          <Route path="/users" element={<Users />} />
          <Route path="/profiles" element={<Profiles />} />
          <Route path="/profiles/:profileId/settings" element={<ProfileSettings />} />
          <Route path="/sources" element={<Sources />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/account" element={<Account />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/devices" element={<Devices />} />
          <Route path="/devices/new" element={<AddDevicePage />} />
          <Route path="/devices/:deviceId" element={<DeviceDetail />} />
          <Route path="/dashboards" element={<Dashboards />} />
          <Route path="/dashboards/:slug" element={<DashboardView />} />

          {/* Admin Routes - Protected by AdminRoute */}
          <Route element={<AdminRoute />}>
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/oauth" element={<OAuthClients />} />
          </Route>
        </Route>
      </Routes>
  );
}

function App() {
  const cc = process.env.REACT_APP_CHAT_KEY;
  useEffect(() => {
    if (cc && cc !== '') {
      Crisp.configure(cc);
    }

    // Sentry
    Sentry.init({
      dsn: "https://87beb97ec60a09c01bbb8f3b44ff20c6@o4508468668989440.ingest.us.sentry.io/4508468671676416",
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      // Tracing
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ["localhost", /^https:\/\/app.zafron\.dev\/api/],
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      // A share token IS the credential and it never expires, so it must not
      // reach an error tracker. Redact it from every URL Sentry records.
      beforeSend(event) {
        return redactShareToken(event);
      },
      beforeSendTransaction(event) {
        return redactShareToken(event);
      },
    });
  }, [cc]);
  

  return (
    <ThemeProvider>
      <AuthProvider>
        <MqttClient>
          <RouterProvider router={router} />
        </MqttClient>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App;
